import { Component } from 'react';

import { dayjs } from '@personal-trainer-app/shared/src/utils/date';

import { reportBoundaryError } from '../reportBoundaryError';

import type { Props, State } from './GlobalErrorBoundary.types';
import type { ErrorInfo } from 'react';

import { env, isDebugMode } from '@/utils';

/**
 * Global Error Boundary Component
 *
 * This component catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the app.
 *
 * Features:
 * - Catches and displays user-friendly error messages
 * - Logs errors with context information
 * - Provides error recovery options
 * - Shows detailed error info in development mode
 * - Reports the error through the observability manager (Sentry)
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    this.logError(error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  override componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private logError = (error: Error, errorInfo: ErrorInfo) => {
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: dayjs().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      environment: env.appEnvironment,
      appVersion: env.appVersion,
    };

    // Log to console in development
    if (isDebugMode) {
      console.group('🚨 Global Error Boundary - Error Caught');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Error Details:', errorDetails);
      console.groupEnd();
    }

    reportBoundaryError(error, errorInfo, 'global');
  };

  private handleRetry = () => {
    // Clear error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private renderErrorDetails = () => {
    if (!isDebugMode || !this.state.error) {
      return null;
    }

    return (
      <details className="mt-6 p-4 bg-error-wash border border-error-500/30 rounded-lg">
        <summary className="font-medium text-error-400 cursor-pointer hover:text-error-300">
          🔧 Developer Info (Click to expand)
        </summary>
        <div className="mt-3 space-y-3 text-error-300">
          <div>
            <h4 className="font-medium text-error-400">Error Message:</h4>
            <p className="text-body-sm font-mono bg-error-500/10 p-2 rounded mt-1">
              {this.state.error.message}
            </p>
          </div>

          {this.state.error.stack && (
            <div>
              <h4 className="font-medium text-error-400">Stack Trace:</h4>
              <pre className="text-label-xs bg-error-500/10 p-2 rounded mt-1 overflow-auto max-h-48">
                {this.state.error.stack}
              </pre>
            </div>
          )}

          {this.state.errorInfo?.componentStack && (
            <div>
              <h4 className="font-medium text-error-400">Component Stack:</h4>
              <pre className="text-label-xs bg-error-500/10 p-2 rounded mt-1 overflow-auto max-h-48">
                {this.state.errorInfo.componentStack}
              </pre>
            </div>
          )}
        </div>
      </details>
    );
  };

  override render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-contrast-950 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-contrast-700 py-8 px-4 shadow sm:rounded-lg sm:px-10">
              {/* Error Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-error-wash rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-error-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
              </div>

              {/* Error Message */}
              <div className="text-center mb-6">
                <h1 className="font-display text-heading-md font-bold text-neutral-50 mb-2">
                  Oops! Something went wrong
                </h1>
                <p className="text-neutral-400">
                  We encountered an unexpected error. Our team has been notified
                  and is working on a fix.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={this.handleRetry}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md text-body-sm font-medium text-neutral-50 bg-contrast-700 hover:bg-lift-3 focus:outline-none focus-visible:ring-state-focus transition-colors"
                >
                  Try Again
                </button>

                <div className="flex space-x-3">
                  <button
                    onClick={this.handleReload}
                    className="flex-1 flex justify-center py-2 px-4 border border-stroke-default rounded-md text-body-sm font-medium text-neutral-400 bg-contrast-600 hover:bg-lift-3 focus:outline-none focus-visible:ring-state-focus transition-colors"
                  >
                    Reload Page
                  </button>

                  <button
                    onClick={this.handleGoHome}
                    className="flex-1 flex justify-center py-2 px-4 border border-stroke-default rounded-md text-body-sm font-medium text-neutral-400 bg-contrast-600 hover:bg-lift-3 focus:outline-none focus-visible:ring-state-focus transition-colors"
                  >
                    Go Home
                  </button>
                </div>
              </div>

              {/* Contact Support */}
              <div className="mt-6 text-center">
                <p className="text-body-sm text-neutral-400">
                  Still having issues?{' '}
                  <a
                    href={`mailto:support@dofit.app?subject=Error Report&body=Error ID: ${this.state.eventId || 'N/A'}`}
                    className="font-medium text-primary-400 hover:text-primary-300"
                  >
                    Contact Support
                  </a>
                </p>
              </div>

              {/* Developer Details */}
              {this.renderErrorDetails()}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
